import { useCallback, useMemo, useRef, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import {
  FileText,
  Search,
  BarChart3,
  Users,
  Loader2,
  ArrowRight,
  Plus,
} from 'lucide-react'
import { motion } from 'motion/react'
import { AppCard } from '@/components/shell/app-card'
import { AnimatedPlaceholder } from '@/components/shell/animated-placeholder'
import { HeaderControls } from '@/components/layout/header-controls'
import { RichMessageInput, type RichMessageInputHandle } from '@/components/polix-ai/rich-message-input'
import { Prism } from '@/components/backgrounds/prism'
import { APP_CARD_THEMES } from '@/lib/app-card-config'
import { useAuthStore } from '@/stores/auth-store'
import { useCreateV2Conversation } from '@/hooks/use-conversations-v2'
import { useDashboardMetrics } from '@/hooks/use-dashboard-metrics'
import type { RichMessagePayload } from '@/types/rich-input'

const PENDING_MESSAGE_KEY = 'polix-ai-pending-message'
const PENDING_RICH_PAYLOAD_KEY = 'polix-ai-pending-rich-payload'

const LAUNCHPAD_HINTS = [
  'Ask poli-x AI to create a policy...',
  'Type @ to mention a connector or data source...',
  'Try: "What attributes do I need for ITAR compliance?"',
  'Try: "Show me RBAC policy templates"',
  'Try: "Explain deny-overrides combining algorithm"',
  'Try: "Create a policy for engineers accessing project docs"',
]

function getTimeGreeting(): string {
  const hour = new Date().getHours()
  if (hour < 12) return 'Good morning'
  if (hour < 18) return 'Good afternoon'
  return 'Good evening'
}

function getFirstName(displayName: string): string {
  return displayName.split(' ')[0]
}

export function LaunchpadPage() {
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()
  const richInputRef = useRef<RichMessageInputHandle>(null)
  const [inputValue, setInputValue] = useState('')
  const { mutate: createConversation, isPending: startingConversation } =
    useCreateV2Conversation()
  const { data: metrics } = useDashboardMetrics()

  const greeting = useMemo(() => {
    const timeGreeting = getTimeGreeting()
    const firstName = user ? getFirstName(user.displayName) : ''
    return firstName ? `${timeGreeting}, ${firstName}` : timeGreeting
  }, [user])

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const handleSubmitRich = useCallback(
    (payload: RichMessagePayload, systemPrompt?: string) => {
      if (!payload.content.trim()) return

      sessionStorage.setItem(PENDING_MESSAGE_KEY, payload.content)
      sessionStorage.setItem(
        PENDING_RICH_PAYLOAD_KEY,
        JSON.stringify({ payload, systemPrompt })
      )

      createConversation(
        { ...(systemPrompt ? { contextType: 'system', context: { systemPrompt } } : {}) },
        {
          onSuccess: (data) => {
            navigate(`/polix-ai/${data.id}`)
          },
        }
      )
    },
    [createConversation, navigate]
  )

  const handleSubmitPlain = useCallback(
    (content: string) => {
      if (!content.trim()) return

      sessionStorage.setItem(PENDING_MESSAGE_KEY, content)
      createConversation(
        {},
        {
          onSuccess: (data) => {
            navigate(`/polix-ai/${data.id}`)
          },
        }
      )
    },
    [createConversation, navigate]
  )

  const pacStat = metrics?.policyCount
    ? `${metrics.policyCount} ${metrics.policyCount === 1 ? 'policy' : 'policies'}`
    : undefined

  const pacAttrStat = metrics?.attributeCount
    ? `${metrics.attributeCount} attrs`
    : undefined

  const combinedPacStat = [pacStat, pacAttrStat].filter(Boolean).join(' / ')

  const showPlaceholder = inputValue.trim().length === 0

  return (
    <div className="relative flex min-h-screen flex-col bg-background">
      {/* Prism background */}
      <div className="pointer-events-none absolute inset-0 z-0">
        <Prism animationType="hover" transparent />
      </div>

      {/* Header */}
      <header className="relative z-10 flex h-14 items-center justify-between border-b border-border/30 bg-background/60 backdrop-blur-sm px-6">
        <div className="flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="size-5"
          >
            <path d="M15 6v12a3 3 0 1 0 3-3H6a3 3 0 1 0 3 3V6a3 3 0 1 0-3 3h12a3 3 0 1 0-3-3" />
          </svg>
          <span className="text-base font-semibold">poli-x</span>
        </div>

        <HeaderControls onLogout={handleLogout} />
      </header>

      {/* Main Content */}
      <main className="relative z-10 flex flex-1 flex-col items-center justify-center px-6 py-12">
        <div className="w-full max-w-4xl space-y-8">
          {/* Hero Section */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="text-center space-y-2"
          >
            <h1 className="text-4xl font-bold tracking-tight">{greeting}</h1>
            <p className="text-muted-foreground">
              Your data protection platform is ready at your command.
            </p>
          </motion.div>

          {/* App Cards Grid — 2-column */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <AppCard
              name="Data Discovery"
              description="Connect to databases and classify sensitive data automatically"
              icon={Search}
              path="/discovery/dashboard"
              theme={APP_CARD_THEMES.discovery}
              index={0}
            />
            <AppCard
              name="Identity Store"
              description="User attributes, IdP sync, and identity management"
              icon={Users}
              path="/identity/dashboard"
              theme={APP_CARD_THEMES.identity}
              index={1}
            />
            <AppCard
              name="Policy Admin Console"
              description="Create, manage, and deploy XACML ABAC policies for your organization"
              icon={FileText}
              path="/pac/dashboard"
              theme={APP_CARD_THEMES.pac}
              stat={combinedPacStat || undefined}
              index={2}
            />
            <AppCard
              name="Analytics & Reporting"
              description="Audit logs, access decisions, reports, and security analytics"
              icon={BarChart3}
              path="/pac/analytics"
              theme={APP_CARD_THEMES.analytics}
              index={3}
            />
          </div>

          {/* Polix AI Prompt Bar — Perplexity-style */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="space-y-5"
          >
            {/* Branding */}
            <div className="text-center">
              <Link
                to="/polix-ai"
                className="inline-flex items-baseline gap-1.5 transition-opacity hover:opacity-80"
              >
                <span className="text-3xl font-bold tracking-tight">poli-x</span>
                <span className="text-3xl font-light tracking-tight text-muted-foreground">AI</span>
              </Link>
            </div>

            {/* Input container */}
            <div className="rounded-2xl border border-border/60 bg-card/80 shadow-sm backdrop-blur-[2px] overflow-hidden">
              <div className="relative">
                <RichMessageInput
                  ref={richInputRef}
                  variant="compact"
                  onSubmit={handleSubmitPlain}
                  onSubmitRich={handleSubmitRich}
                  onValueChange={setInputValue}
                  disabled={startingConversation}
                  placeholder=""
                />
                <AnimatedPlaceholder
                  hints={LAUNCHPAD_HINTS}
                  intervalMs={3500}
                  visible={showPlaceholder}
                />
              </div>

              {/* Bottom toolbar */}
              <div className="flex items-center justify-between px-3 py-2">
                <button
                  type="button"
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border/60 text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
                  aria-label="Attach"
                >
                  <Plus className="h-4 w-4" />
                </button>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-foreground text-background transition-opacity disabled:opacity-40"
                    aria-label="Send"
                    disabled={startingConversation}
                    onClick={() => richInputRef.current?.submit()}
                  >
                    {startingConversation ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <ArrowRight className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  )
}
